/**
 * toast.success(msg) / toast.error(msg), on ds-zta's Toast (cascade plan 4.4).
 *
 * ZTA's 98 calls were react-hot-toast's, and several sit outside React (the axios
 * interceptor in services/api.ts). This keeps that exact call shape, so the swap is
 * an import change, and forwards each call to the one <ToastProvider> main.tsx
 * mounts. <ToastBridge /> inside the provider connects the two; a call made before
 * it mounts is held and shown once it does.
 *
 * Only the two methods the app uses exist. New code in a component can call
 * useToast() from ds-zta directly for titles, warnings and actions.
 */
import { useEffect, type ReactNode } from 'react'
import { useToast, type ToastOptions } from '@/components/ds-zta'

type Show = (opts: ToastOptions) => string

let sink: Show | null = null
const pending: ToastOptions[] = []

function show(opts: ToastOptions): string {
  if (sink) return sink(opts)
  pending.push(opts)
  return ''
}

export const toast = {
  success: (message: ReactNode) => show({ description: message, variant: 'success' }),
  error: (message: ReactNode) => show({ description: message, variant: 'danger' }),
}

export default toast

/** Mount once, inside <ToastProvider>. Renders nothing. */
export function ToastBridge(): null {
  const { toast: showToast } = useToast()
  useEffect(() => {
    sink = showToast
    for (const opts of pending.splice(0)) showToast(opts)
    return () => {
      if (sink === showToast) sink = null
    }
  }, [showToast])
  return null
}
